/**
 * Core
 */

import { routeTicketEnum } from '@core/router/routeNames';

/**
 * Access
 */

import type { TicketAccessContext } from '@modules/tickets/access/ticketAccessContext';
import { isTicketRouteAllowed } from '@modules/tickets/access/ticketRouteAccess';

/**
 * Types
 */

import type { TicketTabRouteName } from '@modules/tickets/composables/primaryTicketTabDefinitions';
import type { TicketLifecycleSource } from '@/modules/tickets/utils/ticketLifecycle';

type TicketRedirectRouteName =
  | TicketTabRouteName
  | typeof routeTicketEnum.TICKET_INFO;

type TicketState = TicketLifecycleSource & {
  code: string;
};

type TicketRedirectRouteLocation = {
  [K in TicketRedirectRouteName]: {
    name: K;
    params: { code: string };
  };
}[TicketRedirectRouteName];

type RedirectRouteLocation =
  | TicketRedirectRouteLocation
  | { name: typeof routeTicketEnum.DASHBOARD };

/**
 * Factory to build a RouteLocation object targeting a ticket.
 */
function buildTicketRouteLocation<N extends TicketRedirectRouteName>(
  name: N,
  code: string,
): TicketRedirectRouteLocation {
  return { name, params: { code } };
}

/**
 * Determines the optimal ticket redirect destination based on user permissions:
 * 1. Preferred route (if provided and allowed; use {@link lifecycleLandingRoute} upstream when appropriate)
 * 2. Ticket information page when the user is a ticket user
 * 3. Dashboard (final fallback)
 */
function resolveTicketDestination(
  context: TicketAccessContext,
  code: string,
  preferredRoute?: TicketRedirectRouteName,
): RedirectRouteLocation {
  if (!code) {
    return { name: routeTicketEnum.DASHBOARD };
  }

  if (preferredRoute && isTicketRouteAllowed(context, preferredRoute)) {
    return buildTicketRouteLocation(preferredRoute, code);
  }

  if (isTicketRouteAllowed(context, routeTicketEnum.TICKET_INFO)) {
    return buildTicketRouteLocation(routeTicketEnum.TICKET_INFO, code);
  }

  return { name: routeTicketEnum.DASHBOARD };
}

export type { TicketRedirectRouteName, RedirectRouteLocation, TicketState };

export { resolveTicketDestination, buildTicketRouteLocation };
